import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { OpsPnlMonth, OpsSaasMonth } from '../../lib/operations-api';
import {
  formatInt,
  formatMoney,
  formatMoneyCompact,
  formatMonthShort,
} from '../../lib/operations';
import { useLocale } from '../../i18n/LocaleProvider';
import {
  AXIS_TICK,
  GRID_STROKE,
  LEGEND_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
  TOOLTIP_STYLE,
} from '../chartTheme';

/**
 * The operations dashboard's charts. Colours: brand teal for revenue and growth, red for what goes
 * out (costs, churn), indigo for the result line — the same neutrals as the queue charts
 * (`chartTheme.ts`) so the tool reads as one.
 */
const C = {
  revenue: '#0d9488',
  costs: '#f87171',
  result: '#4f46e5',
  newMrr: '#0d9488',
  reactivation: '#5eead4',
  expansion: '#0ea5e9',
  contraction: '#f59e0b',
  churn: '#dc2626',
  installs: '#0d9488',
  uninstalls: '#f87171',
  paying: '#4f46e5',
} as const;

const HEIGHT = 260;

/** Net revenue vs. operating costs, with EBITDA as a line. */
export function RevenueCostChart({
  months,
}: {
  readonly months: readonly OpsPnlMonth[];
}) {
  const { t, locale } = useLocale();
  const data = months.map((m) => ({
    label: formatMonthShort(m.month, locale),
    revenue: m.revenueNet,
    costs: -(m.cogs + m.opex),
    ebitda: m.ebitda,
  }));
  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <ComposedChart
        data={data}
        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
        stackOffset="sign"
      >
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis
          dataKey="label"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={72}
          tickFormatter={(v) => formatMoneyCompact(Number(v), locale)}
        />
        <ReferenceLine y={0} stroke="#9ca3af" />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={(value, name) => [
            formatMoney(Number(value), locale),
            String(name),
          ]}
        />
        <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" iconSize={8} />
        <Bar
          dataKey="revenue"
          name={t('operations.charts.revenueNet')}
          fill={C.revenue}
          radius={[3, 3, 0, 0]}
        />
        <Bar
          dataKey="costs"
          name={t('operations.charts.costs')}
          fill={C.costs}
          radius={[0, 0, 3, 3]}
        />
        <Line
          dataKey="ebitda"
          name={t('operations.charts.ebitda')}
          stroke={C.result}
          strokeWidth={2}
          dot={{ r: 2 }}
          type="monotone"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** MRR movements per month: what came in above the axis, what went out below, net as a line. */
export function MrrMovementChart({
  months,
}: {
  readonly months: readonly OpsSaasMonth[];
}) {
  const { t, locale } = useLocale();
  const data = months.map((m) => ({
    label: formatMonthShort(m.month, locale),
    newMrr: m.newMrr,
    reactivation: m.reactivationMrr,
    expansion: m.expansionMrr,
    contraction: -m.contractionMrr,
    churn: -m.churnedMrr,
    net: m.netNewMrr,
  }));
  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <ComposedChart
        data={data}
        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
        stackOffset="sign"
      >
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis
          dataKey="label"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={72}
          tickFormatter={(v) => formatMoneyCompact(Number(v), locale)}
        />
        <ReferenceLine y={0} stroke="#9ca3af" />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={(value, name) => [
            formatMoney(Number(value), locale),
            String(name),
          ]}
        />
        <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" iconSize={8} />
        <Bar
          dataKey="newMrr"
          stackId="m"
          name={t('operations.charts.newMrr')}
          fill={C.newMrr}
        />
        <Bar
          dataKey="reactivation"
          stackId="m"
          name={t('operations.charts.reactivationMrr')}
          fill={C.reactivation}
        />
        <Bar
          dataKey="expansion"
          stackId="m"
          name={t('operations.charts.expansionMrr')}
          fill={C.expansion}
        />
        <Bar
          dataKey="contraction"
          stackId="m"
          name={t('operations.charts.contractionMrr')}
          fill={C.contraction}
        />
        <Bar
          dataKey="churn"
          stackId="m"
          name={t('operations.charts.churnedMrr')}
          fill={C.churn}
        />
        <Line
          dataKey="net"
          name={t('operations.charts.netNewMrr')}
          stroke={C.result}
          strokeWidth={2}
          dot={{ r: 2 }}
          type="monotone"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Installs and uninstalls per month, with paying shops at month end on a second axis. */
export function GrowthChart({
  months,
}: {
  readonly months: readonly OpsSaasMonth[];
}) {
  const { t, locale } = useLocale();
  const data = months.map((m) => ({
    label: formatMonthShort(m.month, locale),
    installs: m.installs + m.reinstalls,
    uninstalls: -m.uninstalls,
    paying: m.payingEnd,
    installed: m.installedEnd,
  }));
  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <ComposedChart
        data={data}
        margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
        stackOffset="sign"
      >
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
        <XAxis
          dataKey="label"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="flow"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
        />
        <YAxis
          yAxisId="stock"
          orientation="right"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
        />
        <ReferenceLine yAxisId="flow" y={0} stroke="#9ca3af" />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={(value, name) => [
            formatInt(Math.abs(Number(value)), locale),
            String(name),
          ]}
        />
        <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" iconSize={8} />
        <Bar
          yAxisId="flow"
          dataKey="installs"
          stackId="f"
          name={t('operations.charts.installs')}
          fill={C.installs}
        />
        <Bar
          yAxisId="flow"
          dataKey="uninstalls"
          stackId="f"
          name={t('operations.charts.uninstalls')}
          fill={C.uninstalls}
        />
        <Line
          yAxisId="stock"
          dataKey="installed"
          name={t('operations.charts.installed')}
          stroke="#9ca3af"
          strokeWidth={2}
          dot={false}
          type="monotone"
        />
        <Line
          yAxisId="stock"
          dataKey="paying"
          name={t('operations.charts.paying')}
          stroke={C.paying}
          strokeWidth={2}
          dot={{ r: 2 }}
          type="monotone"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** The funnel right now as one horizontal bar per bucket. */
export function FunnelChart({
  buckets,
}: {
  readonly buckets: readonly {
    readonly label: string;
    readonly value: number;
  }[];
}) {
  const { locale } = useLocale();
  return (
    <ResponsiveContainer width="100%" height={HEIGHT}>
      <BarChart
        data={[...buckets]}
        layout="vertical"
        margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={GRID_STROKE}
          horizontal={false}
        />
        <XAxis
          type="number"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          width={110}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          itemStyle={TOOLTIP_ITEM_STYLE}
          formatter={(value) => [formatInt(Number(value), locale)]}
        />
        <Bar dataKey="value" fill={C.revenue} radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
